/**
 * Seeded PRNG based on mulberry32.
 * Reference: https://gist.github.com/tommyettinger/46a874533244883189143505d203312c
 *
 * Produces deterministic sequences given a seed.
 * Used for gameplay randomness (draws, crits, spawns, map-gen)
 * so runs are reproducible given the same seed.
 */

export interface Rng {
  /** Returns a float in [0, 1) — drop-in replacement for Math.random(). */
  next(): number;
  /** Returns an integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** The seed this RNG was created with. */
  readonly seed: number;
}

/**
 * Create a seeded PRNG (mulberry32).
 * Deterministic: same seed always produces the same sequence.
 */
export function createRng(seed: number): Rng {
  let state = seed | 0;

  function next(): number {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  function int(maxExclusive: number): number {
    return Math.floor(next() * maxExclusive);
  }

  return { next, int, seed };
}

/** Generate a random seed for when none is supplied. */
export function randomSeed(): number {
  return (Math.random() * 4294967296) >>> 0;
}
